import { computed, InputSignal, Signal, Type } from "@angular/core";

/**
 * Represents the state of a form submission in the AI chat system.
 * - 'idle': The initial state where the user can fill out the form.
 * - 'submitting': The form is being submitted, input fields are locked, and a loading indicator is shown.
 * - 'submitted': The form has been submitted successfully, and input fields can no longer be edited.
 * 
 * If an error occurs, the state should be set back to 'idle' to allow the user to correct the issue and try again.
 */
export type AIFormSubmissionState =  'idle' | 'submitting' | 'submitted';

/**
 * Utility function to determine if the form should be blocked based on the submission state.
 */
export function shouldFormBeBlocked(submissionState: Signal<AIFormSubmissionState>): Signal<boolean> {
  return computed(() => submissionState() != 'idle');
}

export type AIFormState = {
  submissionState: AIFormSubmissionState;
  errorMessage?: string | null;
};

export interface AIForm {
  data: InputSignal<any>;
  update: InputSignal<(newData: any) => void>;
}

export type AIStandardMessage = {
  type: 'standard';
  role: 'user' | 'assistant' | 'system' | 'tool-response';
  content: string;
  requiresFollowUp: boolean;
}

export type AIComponentMessage = {
  type: 'component';
  role: 'user' | 'assistant' | 'system';
  component: Type<AIForm>;
  contentForLLM: string;
  data: any;
  requiresFollowUp: boolean;
}

export type AIToolCallMessage = {
  type: 'tool-call';
  role: 'assistant';
  content: string;
  requiresFollowUp: boolean;
}

export type AIToolResponseMessage = {
  type: 'tool-response';
  role: 'user';
  content: string;
  requiresFollowUp: boolean;
}

export type AIChatMessage = AIStandardMessage | AIComponentMessage | AIToolCallMessage | AIToolResponseMessage;

export type UploadedFileContext = {
  name: string;
  type: string;
  size: number;
  contentExcerpt: string;
  isTruncated: boolean;
};
